import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, AlertTriangle, Shield, TrendingUp, Globe } from 'lucide-react';

const RISK_COLORS = { high: '#E24B4A', medium: '#E8A020', low: '#1D9E75' };
const TIER_COLORS = { 1: '#1D9E75', 2: '#1A4C7C', 3: '#E8A020', 4: '#5C6B7A' };

export default function HeatmapPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get("/heatmap").then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div data-testid="heatmap-loading"><Skeleton className="h-96 rounded-sm" /></div>;

  const summary = data?.summary || {};
  const tiers = data?.tier_summary || {};
  let locations = data?.locations || [];
  if (filter === 'au') locations = locations.filter(l => l.country === 'AU');
  else if (filter === 'nz') locations = locations.filter(l => l.country === 'NZ');
  else if (filter === 'high') locations = locations.filter(l => l.risk_level === 'high');

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>Portfolio Heatmap</h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>Tiger ANZ 2000 — Geographic Risk Concentration</p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Locations', value: summary.total_locations, icon: MapPin, color: '#1A4C7C' },
          { label: 'AU Locations', value: summary.au_locations, icon: Globe, color: '#1A4C7C' },
          { label: 'NZ Locations', value: summary.nz_locations, icon: Globe, color: '#00B4D8' },
          { label: 'High Risk', value: summary.high_risk_locations, icon: AlertTriangle, color: '#E24B4A' },
          { label: 'Total Premium', value: `$${(summary.total_premium || 0).toLocaleString()}`, icon: TrendingUp, color: '#1D9E75' },
        ].map((kpi, i) => (
          <Card key={kpi.label} className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
              <span className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{kpi.label}</span>
            </div>
            <p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Tier Distribution */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="tier-distribution">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#0D1B2A' }}>Tiger ANZ 2000 Tier Distribution</h3>
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(tier => (
            <div key={tier} className="relative overflow-hidden p-4 rounded-sm" style={{ background: '#F5F7FA' }}>
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: TIER_COLORS[tier] }} />
              <p className="text-xs font-semibold ml-2" style={{ color: '#0D1B2A' }}>Tier {tier}</p>
              <p className="text-2xl font-bold font-mono ml-2 mt-1" style={{ color: '#0D1B2A' }}>{tiers[tier]?.locations || 0}</p>
              <p className="text-[10px] ml-2" style={{ color: '#5C6B7A' }}>{tiers[tier]?.entities || 0} entities</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All Locations' },
          { id: 'au', label: 'Australia' },
          { id: 'nz', label: 'New Zealand' },
          { id: 'high', label: 'High Risk Only' },
        ].map(f => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f.id ? 'text-white' : 'text-[#5C6B7A] hover:text-[#0D1B2A]'}`}
            style={filter === f.id ? { background: '#1A4C7C' } : { background: 'rgba(136,150,168,0.1)' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Heatmap */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden" data-testid="heatmap-map">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Geographic Heatmap</h3>
          <span className="text-[10px]" style={{ color: '#5C6B7A' }}>Circle size = entity concentration · colour = risk level</span>
        </div>
        <MapContainer center={[-33.5, 157]} zoom={4} style={{ height: 480, width: '100%' }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          {locations.filter(l => l.lat && l.lng).map(loc => {
            const maxEntities = Math.max(...locations.map(x => x.entity_count || 1), 1);
            const radius = 8 + Math.sqrt((loc.entity_count || 1) / maxEntities) * 30;
            const color = RISK_COLORS[loc.risk_level] || '#5C6B7A';
            return (
              <CircleMarker key={loc.city} center={[loc.lat, loc.lng]} radius={radius}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.35, weight: 1.5 }}>
                <Popup>
                  <div style={{ fontSize: 12, minWidth: 160 }}>
                    <b>{loc.city}</b> · {loc.state}, {loc.country}<br />
                    Tier {loc.tier} · <span style={{ color, fontWeight: 700, textTransform: 'uppercase' }}>{loc.risk_level} risk</span><br />
                    {loc.entity_count} entities · {loc.policy_count} policies<br />
                    Premium ${Number(loc.total_premium || 0).toLocaleString()}<br />
                    Hazard {loc.avg_hazard_score} · Crime {loc.avg_crime_index}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Card>

      {/* Location Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="location-grid">
        {locations.map((loc) => (
          <Card key={loc.city} data-testid={`location-${loc.city}`}
            className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden hover:-translate-y-[1px] transition-all">
            <div className="flex">
              <div className="w-1.5" style={{ background: RISK_COLORS[loc.risk_level] }} />
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5" style={{ color: '#1A4C7C' }} />
                      <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{loc.city}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(136,150,168,0.1)', color: '#5C6B7A' }}>
                        {loc.state}, {loc.country}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: TIER_COLORS[loc.tier] + '15', color: TIER_COLORS[loc.tier] }}>
                        Tier {loc.tier}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold"
                        style={{ background: RISK_COLORS[loc.risk_level] + '15', color: RISK_COLORS[loc.risk_level] }}>
                        {loc.risk_level} risk
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{loc.entity_count}</p>
                    <p className="text-[9px]" style={{ color: '#5C6B7A' }}>entities</p>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-4 gap-2 text-[10px]" style={{ color: '#5C6B7A' }}>
                  <div><p>Policies</p><p className="font-bold text-xs" style={{ color: '#0D1B2A' }}>{loc.policy_count}</p></div>
                  <div><p>Premium</p><p className="font-bold text-xs" style={{ color: '#0D1B2A' }}>${loc.total_premium.toLocaleString()}</p></div>
                  <div><p>Hazard</p><p className="font-bold text-xs" style={{ color: loc.avg_hazard_score > 50 ? '#E24B4A' : '#0D1B2A' }}>{loc.avg_hazard_score}</p></div>
                  <div><p>Crime</p><p className="font-bold text-xs" style={{ color: loc.avg_crime_index > 60 ? '#E24B4A' : '#0D1B2A' }}>{loc.avg_crime_index}</p></div>
                </div>

                {loc.claims_pct > 0 && (
                  <div className="mt-2 text-[10px] flex items-center gap-1" style={{ color: '#E8A020' }}>
                    <AlertTriangle className="w-3 h-3" /> {loc.claims_pct}% entities with claims
                  </div>
                )}

                {loc.top_industries?.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {loc.top_industries.map((ind, i) => (
                      <span key={ind.name} className="text-[9px] px-1 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#5C6B7A' }}>
                        {ind.name} ({ind.count})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Risk Legend */}
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="flex items-center gap-6 text-xs">
          <span className="font-semibold" style={{ color: '#0D1B2A' }}>Risk Score =</span>
          <span style={{ color: '#5C6B7A' }}>Hazard (40%) + Crime (30%) + Claims % (30%)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#1D9E75' }} /> Low (&lt;30)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#E8A020' }} /> Medium (30-50)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#E24B4A' }} /> High (&gt;50)</span>
        </div>
      </Card>
    </div>
  );
}
